/**
 * launch.ts — web->app handoff: build the helm:// deep link and invoke the OS
 * protocol handler that launches the native Helm host client, with heuristic
 * detection of "the app isn't installed" so the UI can fall back to the browser.
 *
 * Only the control-plane base URL + the one-time launch token travel in the URL.
 * The target device + mode are bound into the signed token server-side, so a
 * tampered link can't retarget the session.
 */

import type { LaunchToken } from '@/types';

/** Build the helm:// deep link from a server-minted launch token. */
export function buildLaunchUrl(lt: LaunchToken): string {
  return `helm://session?server=${encodeURIComponent(lt.server_url)}&token=${encodeURIComponent(lt.token)}`;
}

/**
 * Invoke a custom-protocol URL and detect failure.
 *
 * There is no reliable synchronous signal that a protocol handler exists, so we use
 * the standard ScreenConnect/Zoom heuristic: navigating to a registered handler
 * makes the page lose focus/visibility (the OS dialog / app takes over). If that
 * happens before the timeout, the launch worked. If the timer elapses while the page
 * is still visible AND focused, assume the app isn't installed and call onFail.
 *
 * Returns a cleanup function (idempotent) that cancels the timer + listeners.
 */
export function launchNativeApp(
  url: string,
  opts: { onFail: () => void; timeoutMs?: number },
): () => void {
  // SSR / non-DOM safety (also keeps unit tests that lack a full DOM happy).
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  const timeoutMs = opts.timeoutMs ?? 2500;
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cleanup = () => {
    if (done) return;
    done = true;
    if (timer !== undefined) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('blur', onHidden);
    window.removeEventListener('pagehide', onHidden);
  };

  // Any of these firing means the OS took over (the handler launched) → success.
  function onHidden() {
    if (document.visibilityState === 'hidden' || document.hidden) {
      cleanup();
    } else {
      // window 'blur' / 'pagehide' also indicate the handler grabbed focus.
      cleanup();
    }
  }

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('blur', onHidden);
  window.addEventListener('pagehide', onHidden);

  timer = setTimeout(() => {
    if (done) return;
    // Still here and visible+focused → the handler almost certainly isn't installed.
    const stillForeground =
      (document.visibilityState === 'visible' || document.hidden === false) && document.hasFocus();
    cleanup();
    if (stillForeground) opts.onFail();
  }, timeoutMs);

  // Trigger the protocol. Assigning location.href is the most broadly compatible way
  // to invoke a custom scheme without navigating away on success.
  try {
    window.location.href = url;
  } catch {
    cleanup();
    opts.onFail();
  }

  return cleanup;
}
